import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { COMPANY } from "@/data/site";

const PURPOSE_TEXT = {
  service:
    "Hi Click World Online! I'm interested in your digital marketing services and would like to discuss a project for my business.",
  careers:
    "Hi Click World Online! I'm interested in career and job opportunities with your team. Could you please share any current openings?",
};

const waLink = (num, purpose) =>
  `https://wa.me/${num.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(PURPOSE_TEXT[purpose] || PURPOSE_TEXT.service)}`;

const WhatsAppFloat = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(null);
  const toggle = () => {
    setOpen(!open);
    setStep(null);
  };
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            data-testid="whatsapp-popup"
            className="bg-navy-light border border-white/10 rounded-2xl p-5 w-72 shadow-2xl shadow-black/60"
          >
            {!step ? (
              <>
                <p className="font-display font-bold text-mist text-lg">How can we help you?</p>
                <p className="text-slatec text-sm mt-1 mb-4">Pick an option — you'll jump straight into a WhatsApp chat with our team.</p>
                <button
                  onClick={() => setStep("service")}
                  data-testid="whatsapp-option-service"
                  className="block w-full text-center bg-[#25D366] hover:bg-[#1fb857] text-navy-deep font-semibold text-sm rounded-full py-3 transition-colors duration-200"
                >
                  I'm looking for a service
                </button>
                <button
                  onClick={() => setStep("careers")}
                  data-testid="whatsapp-option-careers"
                  className="mt-2 block w-full text-center border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10 font-semibold text-sm rounded-full py-3 transition-colors duration-200"
                >
                  I'm exploring job opportunities
                </button>
              </>
            ) : (
              <>
                <p className="font-display font-bold text-mist text-lg">
                  {step === "service" ? "Let's talk about your project." : "Let's talk about joining us."}
                </p>
                <p className="text-slatec text-sm mt-1 mb-4">Start the WhatsApp chat — we'll pick it up from there.</p>
                <a
                  href={waLink(COMPANY.phone1, step)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-number-1"
                  className="block w-full text-center bg-[#25D366] hover:bg-[#1fb857] text-navy-deep font-semibold text-sm rounded-full py-3 transition-colors duration-200"
                >
                  WhatsApp {COMPANY.phone1}
                </a>
                <p className="mt-4 mb-2 text-xs uppercase tracking-[0.18em] text-mist/40 font-semibold text-center">Or call us directly</p>
                <a
                  href={`tel:${COMPANY.phone1.replace(/\s/g, "")}`}
                  data-testid="whatsapp-call-1"
                  className="block w-full text-center border border-white/15 text-mist/85 hover:border-electric hover:text-electric font-semibold text-sm rounded-full py-2.5 transition-colors duration-200"
                >
                  Call {COMPANY.phone1}
                </a>
                <a
                  href={`tel:${COMPANY.phone2.replace(/\s/g, "")}`}
                  data-testid="whatsapp-call-2"
                  className="mt-2 block w-full text-center border border-white/15 text-mist/85 hover:border-electric hover:text-electric font-semibold text-sm rounded-full py-2.5 transition-colors duration-200"
                >
                  Call {COMPANY.phone2}
                </a>
                <button
                  onClick={() => setStep(null)}
                  data-testid="whatsapp-back-button"
                  className="mt-3 block mx-auto text-xs text-mist/50 hover:text-electric transition-colors"
                >
                  ← Back to options
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        data-testid="whatsapp-float-button"
        onClick={toggle}
        aria-label={open ? "Close WhatsApp chat options" : "Chat on WhatsApp"}
        className="w-14 h-14 rounded-full bg-[#25D366] text-navy-deep flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:scale-105 transition-transform duration-300"
      >
        {open ? <X size={24} /> : <MessageCircle size={26} />}
      </button>
    </div>
  );
};

export default WhatsAppFloat;
